/* AdGate — verrou de vue de campagne, version épurée.
   Le formulaire reste verrouillé pendant `seconds` (le temps de voir la pub
   affichée au-dessus). Simple barre de progression + compte à rebours ; à la
   fin, déverrouillage auto via onComplete. */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Lock } from "lucide-react";
import { fillTemplate } from "@/lib/defaults";

export default function AdGate({ seconds = 20, onComplete, texts = {} }) {
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const startRef = useRef(null);

  useEffect(() => {
    let raf;
    const tick = (t) => {
      if (startRef.current == null) startRef.current = t;
      const e = (t - startRef.current) / 1000;
      const capped = Math.min(seconds, e);
      setElapsed(capped);
      if (capped < seconds) {
        raf = requestAnimationFrame(tick);
      } else if (!done) {
        setDone(true);
        // Pas de navigator.vibrate ici : le déverrouillage est automatique
        // (fin du compte à rebours), donc sans geste utilisateur. Chrome
        // bloque alors l'appel et journalise une « Intervention ». On s'abstient.
        onComplete && onComplete();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  const pct = Math.min(100, (elapsed / seconds) * 100);
  const remaining = Math.max(0, Math.ceil(seconds - elapsed));

  return (
    <div data-testid="ad-gate" className="w-full rounded-2xl border border-border bg-card px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {done ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: "var(--color-brand)" }} />
          ) : (
            <Lock className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          )}
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground truncate">
            {done ? texts.gateValidatedLabel : texts.gateBonusLabel || "Publicité sponsor"}
          </span>
        </div>
        <AnimatePresence mode="wait">
          {!done && (
            <motion.span
              key="cd"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="ad-gate-countdown"
              className="text-xs font-mono font-bold tabular-nums flex-shrink-0"
              style={{ color: "var(--color-brand)" }}
            >
              {remaining}s
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Barre de progression simple */}
      <div className="relative mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${pct}%`,
            background: done
              ? "linear-gradient(90deg, var(--color-brand), var(--color-accent))"
              : "var(--color-brand)",
            transition: "width 80ms linear, background 300ms ease",
          }}
        />
      </div>

      {/* Indice sous la barre, tant que verrouillé */}
      <AnimatePresence>
        {!done && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              {fillTemplate(texts.gateLockedHint, { seconds: remaining })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
